
import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../base/Button';
import Input from '../base/Input';

interface LoginFormProps {
  onToggleMode: () => void;
}

export default function LoginForm({ onToggleMode }: LoginFormProps) {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { error } = await signIn(email.trim().toLowerCase(), password);
      
      if (error) {
        console.error('Login error:', error);
        
        if (error.message === 'Invalid login credentials') {
          setError('Ungültige E-Mail-Adresse oder Passwort. Bitte überprüfen Sie Ihre Eingaben.');
        } else if (error.message === 'Email not confirmed') {
          setError('Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse. Überprüfen Sie Ihren Posteingang.');
        } else if (error.message.includes('too many requests')) {
          setError('Zu viele Anmeldeversuche. Bitte warten Sie einen Moment und versuchen Sie es erneut.');
        } else {
          setError(`Anmeldefehler: ${error.message}`);
        }
        setLoading(false);
      } else {
        // Erfolgreiche Anmeldung - Weiterleitung zum Dashboard
        console.log('Login successful, redirecting...');
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie erneut.');
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8">
      <div className="text-center mb-6">
        <div 
          className="text-2xl font-bold text-gray-900 mb-2"
          style={{ fontFamily: '"Pacifico", serif' }}
        >
          Dä Snäckomat Manager
        </div>
        <h2 className="text-xl font-semibold text-gray-700">Anmelden</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            E-Mail-Adresse
          </label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ihre-email@example.com"
            required
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Passwort
          </label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Ihr Passwort"
            required
            disabled={loading}
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <div className="flex">
              <i className="ri-error-warning-line w-5 h-5 flex items-center justify-center text-red-400 mr-2"></i>
              <div>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          </div>
        )}

        <Button
          type="submit"
          className="w-full"
          disabled={loading}
        >
          {loading ? (
            <>
              <i className="ri-loader-line w-4 h-4 flex items-center justify-center animate-spin mr-2"></i>
              Wird angemeldet...
            </>
          ) : (
            'Anmelden'
          )}
        </Button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          Noch kein Konto?{' '}
          <button
            onClick={onToggleMode}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Jetzt registrieren
          </button>
        </p>
        <p className="text-xs text-gray-500 mt-2">
          Probleme beim Anmelden? Stellen Sie sicher, dass Sie Ihre E-Mail-Adresse bestätigt haben.
        </p>
      </div>
    </div>
  );
}
